import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDebounce } from '../../utils/useDebounce';
import PropTypes from 'prop-types';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Affix,
  Button,
  Form,
  Row,
  Spin,
  Steps,
  message as antMessage,
  notification,
  Typography
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import PageHeader from '../../components/PageHeader/PageHeader';
import ContentCustom from '../../components/ContentCustom/ContentCustom';
import { verticalLayout } from '../../utils/constants/formLayout';
import useErrorMessage from '../../utils/ErrorMessage';
import useAuthContext from '../../contexts/AuthContext';
import useGenerateFormItem from '../../utils/GenerateFormItem';
import useStepsByType from './stepsByType';
import EstimationCard from './EstimationCard';
import checkExemption, { EXCLUSION_EXEMPTION_ITEM } from './checkExemption';
import useQuotesContext from './QuotesContext';
import { applyRcngawkRules } from './useRcnawkRules';
import ExemptionModal from './ExemptionModal';
import RcngawkExemptionModal from './RcngawkExemptionModal';
import { routes, subRoutes } from '../../utils/constants/routes';
// import InfosBar from './InfosBar';
import useDefaultValues from './defaultValues';
import { AnalyzeModal } from './AnalyzeModal';
import { dromComAllowedTypes } from './lastStepFields';

import { ExclusionModal } from './ExclusionModal';

const CreateUpdateQuote = ({ purpose }) => {
  const { subPath: type, id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI, user } = useAuthContext();
  const {
    exemptions,
    setExemptions,
    formValues,
    setFormValues,
    setInseeLoading,
    getEstimation,
    setInitialContractValues,
    initialContractValues,
    siretStatus,
    rnaStatus,
    setRnaStatus,
    setSiretStatus,
    codesNaf,
    setAnalyzeModalVisible,
    dataModal,
    setDataModal,
    estimationAvoider,
    estimatedCost,
    eligiblePricing,
    errorGetCities,
    activitiesHiscox
  } = useQuotesContext();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const supplier = (params.get('sup') ?? '').trim();
  const mrpType = params.get('mrpT');
  const assoType = params.get('assoT');
  const goToStep = params.get('go-to-step');
  const [file, setFile] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const generateFields = useGenerateFormItem();
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [modalExemption, setModalExemption] = useState(false);
  const { steps } = useStepsByType(form, purpose, current, type);
  const defaultValues = useDefaultValues({ type, mrpType, assoType, supplier });
  const [isOkCustomer, setIsOkCustomer] = useState(true);
  const [lockFirstEstimation, setLockFirstEstimation] = useState(true);
  const [isQPV, setIsQPV] = useState(false);
  const [errorGetQPV, setErrorGetQPV] = useState(false);
  const debouncedFormValues = useDebounce(formValues, 500);

  const checkQPVForSites = async () => {
    const validSitesOrBuildings =
      (formValues?.sites?.length > 0
        ? formValues?.sites
        : formValues?.buildings) || [];
    const siteRequests = validSitesOrBuildings
      .filter((site) => {
        const { address } = site;
        return (
          address?.number &&
          address?.street &&
          address?.city &&
          address?.postal_code
        );
      })
      .map(async (site) => {
        try {
          const { address } = site;
          const { number, street, city, postal_code } = address;
          const url = `/insee/qpv?number=${number}&street=${encodeURIComponent(
            street
          )}&city=${encodeURIComponent(city)}&zipcode=${postal_code}`;

          const response = await dispatchAPI('GET', { url });
          if (response?.data?.isQPV) {
            return true;
          }

          return false;
        } catch (error) {
          setErrorGetQPV(true);
          return false;
        }
      });

    const results = await Promise.all(siteRequests);
    const isAnyQPV = results.includes(true);
    setIsQPV(isAnyQPV);
  };

  const sitesChanged = useMemo(() => formValues?.sites, [formValues?.sites]);
  const buildingsChanged = useMemo(
    () => formValues?.buildings,
    [formValues?.buildings]
  );

  useEffect(() => {
    if (isQPV) {
      notification.warning({
        message: t('contracts.messages.qpv'),
        duration: 30
      });
    }
    if (formValues) {
      setExemptions(
        checkExemption({
          type,
          values: formValues,
          initialContractValues,
          codesNaf,
          supplier,
          estimation: estimatedCost,
          errorGetCities,
          errorGetQPV,
          isQPV,
          activitiesHiscox
        })
      );
    }
  }, [isQPV]);

  useEffect(() => {
    if (
      formValues?.under_construction_duration === 'under_construction_plus4'
    ) {
      notification.error({
        message: t('help.not_eligible_pricing_title'),
        description: t('help.not_eligible_pricing'),
        duration: 30,
        key: 'oneTime'
      });
    }
  }, [formValues?.under_construction_duration]);

  useEffect(() => {
    if (errorGetQPV) {
      notification.warning({
        message: t('contracts.messages.error_get_qpv'),
        duration: 30
      });
    }
  }, [errorGetQPV]);

  useEffect(() => {
    if (
      (formValues?.sites?.length > 0 &&
        type === 'mrp' &&
        (supplier === 'ml' || supplier === 'wk')) ||
      (formValues?.buildings?.length > 0 && type === 'mriml')
    ) {
      checkQPVForSites();
    }
  }, [sitesChanged, buildingsChanged]);

  const [size, setSize] = useState({
    width: window.innerWidth
  });
  const chosenPckg = formValues?.[formValues?.package];
  const hasExemption = formValues?.exemption;

  const createUpdateResource = async (
    forcedExemption,
    update = false,
    tracfin = false
  ) => {
    setIsLoading(true);
    const url = update ? `contracts/${id.split('?')[0]}` : 'contracts';
    const method = update ? 'PATCH' : 'POST';
    const bodyType = supplier
      ? type.concat(supplier).toUpperCase()
      : type.toUpperCase();
    if (
      siretStatus ||
      rnaStatus ||
      type === 'mri' ||
      type === 'mriml' ||
      type === 'mriwk' ||
      type === 'pnoml' ||
      (type === 'prevoyance' && formValues.customer_type === 'INDIVIDUAL')
    ) {
      try {
        const formData = new FormData();
        file.forEach((f) => formData.append('file', f));
        formData.append(
          'values',
          JSON.stringify({
            ...formValues,
            purpose,
            ...(purpose === 'amendment'
              ? { previous_contract: type !== 'mrp' ? id : id.split('?')[0] }
              : {}),
            amendment: undefined,
            type: bodyType,
            insurer: user?.insurer,
            assignee: user?.insurer,
            claims:
              formValues.claims && formValues.claims[0] === ''
                ? undefined
                : formValues.claims,
            exemptions: [
              ...exemptions,
              ...(forcedExemption ? ['forcedExemption'] : []),
              ...(tracfin ? ['tracfin'] : [])
            ],
            ...chosenPckg,
            deductible_amount: formValues.deductible_amount,
            termination_by_company: formValues.termination_by_company,
            activities: formValues.activities,
            turnover: formValues.turnover,
            sites: formValues.sites,
            employees_number: formValues.employees_number,
            total_members: formValues.total_members,
            representatives_number: formValues.representatives_number,
            resident_status: formValues.resident_status,
            yearly_events: formValues.yearly_events,
            number_claims: formValues.number_claims,
            fraction_type: formValues.fraction_type,
            payment_type: formValues.payment_type,
            political_activity: form.political_activity,
            litigation_responsibility: formValues.litigation_responsibility,
            blocked_activities: formValues.blocked_activities,
            annual_budget: formValues.annual_budget,
            max_claims: formValues.max_claims,
            confirm_claims: formValues.confirm_claims,
            is_a_palace: formValues.is_a_palace,
            mall: formValues.mall,
            is_in_sncf_station: formValues.is_in_sncf_station,
            monument: formValues.monument,
            closed_more_60_days: formValues.closed_more_60_days,
            embassy_bowling_disco: formValues.embassy_bowling_disco,
            industry_less_10m: formValues.industry_less_10m,
            close_help: formValues.close_help,
            altitude_location: formValues.altitude_location,
            renovation: formValues.renovation,
            had_waived_certain_remedies_in_its_lease:
              formValues.had_waived_certain_remedies_in_its_lease,
            disaffected: formValues.disaffected,
            zone_ind_com_art: formValues.zone_ind_com_art,
            metropolitan_france: formValues.metropolitan_france,
            built_covered_hard_materials:
              formValues.built_covered_hard_materials,
            take_care_building: formValues.take_care_building,
            extinguisher_conformed: formValues.extinguisher_conformed,
            more_than_2_years: formValues.more_than_2_years,
            no_merge_takeover: formValues.no_merge_takeover,
            stock_exchange_listing: formValues.stock_exchange_listing,
            insolvency_proceeding: formValues.insolvency_proceeding,
            positive_last_2_tax_result: formValues.positive_last_2_tax_result,
            positive_last_2_equity: formValues.positive_last_2_equity,
            last_5_years_blame: formValues.last_5_years_blame,
            guarantee_limit: formValues.guarantee_limit,
            employer_protection: formValues.employer_protection,
            cyber_protection: formValues.cyber_protection,
            holding_protection: formValues.holding_protection,
            key_person_protection: formValues.key_person_protection
          })
        );
        const {
          data: { _id, status, exemption }
        } = await dispatchAPI(method, {
          url,
          body: formData
        });
        if (status === 'QUOTE') {
          if (exemption)
            antMessage.success(t('basiclayout.submenu.exemption_saved'));
          else antMessage.success(t('basiclayout.submenu.quotes_saved'));
        }
        setFormValues(null);
        setExemptions([]);
        setAnalyzeModalVisible(false);
        navigate(
          !exemption
            ? `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_SAVED}/show/${_id}`
            : `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_IN_EXEMPTION}/show/${_id}`
        );
        document
          .getElementById('mainContent')
          .scrollTo({ behavior: 'smooth', top: 0 });
      } catch (e) {
        if (e.response.statusText === 'Unauthorized') {
          setAnalyzeModalVisible(false);
          setIsLoading(false);
          antMessage.error(
            'Email déjà utilisé, veuillez entrer une nouvelle adresse mail'
          );
        }
        if (e.response) message(e.response.status);
        setIsLoading(false);
      }
    } else {
      antMessage.error(t('login.warning_siret'));
      setIsLoading(false);
    }
  };
  const getResource = useCallback(async () => {
    const idSplit = id.split('?')[0];
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `contracts/${idSplit}`
      });
      const dataPckg = {
        fire_explosion_attack_climatic_event:
          data.fire_explosion_attack_climatic_event,
        theft_vandalism: data.theft_vandalism,
        water_damage: data.water_damage,
        broken_windows: data.broken_windows,
        electric_damage: data.electric_damage,
        ...(type === 'mrp'
          ? {
              broken_machine: data.broken_machine,
              external_furniture: data.external_furniture,
              chilled_merchandise: data.chilled_merchandise,
              legal_protection_mrp: data.legal_protection_mrp,
              extended_legal_protection_mrp: data.extended_legal_protection_mrp,
              assistance: data.assistance,
              archives_capital: data.archives_capital,
              value_capital: data.value_capital,
              art_capital: data.art_capital,
              theft_capital: data.theft_capital,
              object_of_value: data.object_of_value,
              owner_insurance: data.owner_insurance,
              exploitation_loss: data.exploitation_loss,
              lent_goods: data.lent_goods,
              exploitation_responsibility: data.exploitation_responsibility,
              social_mandate_responsibility: data.social_mandate_responsibility,
              rc_pro: data.rc_pro
            }
          : {}),
        ...(type === 'association'
          ? {
              legal_protection: data.legal_protection,
              civil_responsibility_asso: data.civil_responsibility_asso,
              local_insurance: data.local_insurance || false,
              social_mandate_responsibility: data.social_mandate_responsibility,
              extended_legal_protection: data.extended_legal_protection,
              classic_accident_insurance: data.classic_accident_insurance,
              reinforced_accident_insurance: data.reinforced_accident_insurance,
              exploitation_loss: data.exploitation_loss || false,
              light_inflatable_structure:
                data.light_inflatable_structure || false
            }
          : {})
      };
      form.setFieldsValue({
        ...data,
        ...(type === 'mrp'
          ? {
              PACKAGE_1:
                data.package === 'PACKAGE_1'
                  ? dataPckg
                  : defaultValues.PACKAGE_1,
              PACKAGE_2:
                data.package === 'PACKAGE_2'
                  ? dataPckg
                  : defaultValues.PACKAGE_2,
              PACKAGE_3:
                data.package === 'PACKAGE_3'
                  ? dataPckg
                  : defaultValues.PACKAGE_3
            }
          : {}),
        ...(type === 'association'
          ? {
              PACKAGE_1:
                data.package === 'PACKAGE_1'
                  ? dataPckg
                  : defaultValues.PACKAGE_1
            }
          : {}),
        construction_year: data.construction_year
          ? moment(data.construction_year)
          : undefined,
        acquisition_date: data.acquisition_date
          ? moment(data.acquisition_date)
          : undefined,
        termination_date_by_company: data.termination_date_by_company
          ? moment(data.termination_date_by_company)
          : undefined,
        last_termination_date: data.last_termination_date
          ? moment(data.last_termination_date)
          : undefined,
        ...(data.claims
          ? {
              claims: data.claims.map((element) => ({
                ...element,
                claim_date: moment(element.claim_date)
              }))
            }
          : {}),
        ...(data.adult_1
          ? {
              adult_1: { ...data.adult_1, dob: moment(data.adult_1.dob) }
            }
          : {}),
        ...(data.adult_2
          ? {
              adult_2: { ...data.adult_2, dob: moment(data.adult_2.dob) }
            }
          : {}),
        ...(data.recipient
          ? {
              recipient: { ...data.recipient, dob: moment(data.recipient) }
            }
          : {}),
        ...(data.children
          ? {
              children: data.children.map((element) => ({
                ...element,
                dob: moment(element.dob)
              }))
            }
          : {}),
        customer: {
          ...data?.customer,
          dob: moment(data.customer.dob),
          company_creation_date: data.customer.company_creation_date
            ? moment(data.customer.company_creation_date)
            : undefined
        }
      });
      setFormValues({
        ...data,
        ...(type === 'mrp'
          ? {
              PACKAGE_1:
                data.package === 'PACKAGE_1'
                  ? dataPckg
                  : defaultValues.PACKAGE_1,
              PACKAGE_2:
                data.package === 'PACKAGE_2'
                  ? dataPckg
                  : defaultValues.PACKAGE_2,
              PACKAGE_3:
                data.package === 'PACKAGE_3'
                  ? dataPckg
                  : defaultValues.PACKAGE_3
            }
          : {}),
        ...(type === 'association'
          ? {
              PACKAGE_1:
                data.package === 'PACKAGE_1'
                  ? dataPckg
                  : defaultValues.PACKAGE_1
            }
          : {}),
        ...data,
        construction_year: data.construction_year
          ? moment(data.construction_year)
          : undefined,
        acquisition_date: data.acquisition_date
          ? moment(data.acquisition_date)
          : undefined,
        termination_date_by_company: data.termination_date_by_company
          ? moment(data.termination_date_by_company)
          : undefined,
        last_termination_date: data.last_termination_date
          ? moment(data.last_termination_date)
          : undefined,
        ...(data.claims
          ? {
              claims: data.claims.map((element) => ({
                ...element,
                claim_date: moment(element.claim_date)
              }))
            }
          : {}),
        ...(data.adult_1
          ? {
              adult_1: { ...data.adult_1, dob: moment(data.adult_1) }
            }
          : {}),
        ...(data.adult_2
          ? {
              adult_2: { ...data.adult_2, dob: moment(data.adult_2) }
            }
          : {}),
        ...(data.children
          ? {
              children: data.children.map((element) => ({
                ...element,
                dob: moment(element.dob)
              }))
            }
          : {}),
        customer: {
          ...data?.customer,
          dob: moment(data.customer.dob)
        }
      });
      if (purpose === 'amendment') {
        setInitialContractValues({
          ...data,
          construction_year: data.construction_year
            ? moment(data.construction_year)
            : undefined,
          termination_date_by_company: data.termination_date_by_company
            ? moment(data.termination_date_by_company)
            : undefined,
          last_termination_date: data.last_termination_date
            ? moment(data.last_termination_date)
            : undefined,
          ...(data.claims
            ? {
                claims: data.claims.map((element) => ({
                  ...element,
                  claim_date: moment(element.claim_date)
                }))
              }
            : {}),
          ...(data.children
            ? {
                children: data.children.map((element) => ({
                  ...element,
                  dob: moment(element.dob)
                }))
              }
            : {}),
          customer: {
            ...data?.customer,
            dob: moment(data.customer.dob)
          }
        });
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  }, [purpose, id]);

  useEffect(() => {
    if (hasExemption && purpose === 'edit') {
      (() => {
        return navigate(
          `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_SAVED}/show/${id}`
        );
      })();
    }
    // eslint-disable-next-line
  }, [hasExemption]);

  useEffect(() => {
    const verifyAndFetchData = async () => {
      if (
        (purpose === 'edit' && id) ||
        (purpose === 'amendment' && id) ||
        (purpose === 'duplicate' && id) ||
        (purpose === 'duplicate_amendment' && id) ||
        (purpose === 'edit_amendment' && id)
      ) {
        setIsLoading(true);
        setSiretStatus(true);
        setRnaStatus(true);
        await getResource();
      }
      setLockFirstEstimation(false);
    };
    verifyAndFetchData();
  }, []);

  // eslint-disable-next-line consistent-return
  const handleSubmit = async (_, forcedExemption = false, tracfin) => {
    if (purpose === 'edit')
      await createUpdateResource(forcedExemption, true, tracfin);
    if (purpose === 'create')
      await createUpdateResource(forcedExemption, false, tracfin);
    if (purpose === 'duplicate')
      await createUpdateResource(forcedExemption, false, tracfin);
    if (purpose === 'duplicate_amendment')
      await createUpdateResource(forcedExemption, false, tracfin);
    if (purpose === 'amendment')
      await createUpdateResource(forcedExemption, false, tracfin);
    if (purpose === 'edit_amendment')
      await createUpdateResource(forcedExemption, true, tracfin);
  };

  const handlePrev = () => {
    setCurrent(current - 1);
    document
      .getElementById('mainContent')
      .scrollTo({ behavior: 'smooth', top: 0 });
  };

  const verifyCustomer = async (code) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/customers?siret=${code}`
      });
      if (data && data.length > 0) {
        setIsOkCustomer(!data[0].blackList);
        return data[0].blackList;
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsOkCustomer(true);
    return false;
  };

  const checkEligibilyCodePostal = (codePostal) => {
    const postalCodesOutsideMetropolitanFrance = ['20', '97', '98'];
    let postalCodeMetropolitanFrance = true;
    postalCodesOutsideMetropolitanFrance.forEach((postalReference) => {
      if (
        codePostal.startsWith(postalReference) &&
        !dromComAllowedTypes.includes(type)
      )
        postalCodeMetropolitanFrance = false;
    });
    return postalCodeMetropolitanFrance;
  };

  const getSiretInfo = async (code) => {
    if (await verifyCustomer(code)) {
      antMessage.error(
        'Il semble que votre prospect(e) n’est pas éligible à nos produits, vous pouvez contacter le siège pour plus de renseignements.'
      );
      return;
    }
    setIsOkCustomer(true);
    setInseeLoading(true);
    try {
      const {
        data: {
          etablissement: {
            siret,
            dateCreationEtablissement,
            adresseEtablissement: {
              numeroVoieEtablissement,
              typeVoieEtablissement,
              libelleVoieEtablissement,
              codePostalEtablissement,
              complementAdresseEtablissement,
              libelleCommuneEtablissement
            },
            uniteLegale: {
              denominationUniteLegale,
              prenomUsuelUniteLegale,
              nomUniteLegale,
              sexeUniteLegale,
              activitePrincipaleUniteLegale,
              etatAdministratifUniteLegale
            }
          }
        }
      } = await dispatchAPI('GET', {
        url: `/insee/${code}`
      });

      let tmpGender;
      if (sexeUniteLegale === 'M') {
        tmpGender = 'MISTER';
      }
      if (sexeUniteLegale === 'F') {
        tmpGender = 'MADAME';
      }

      let tmpStreet = libelleVoieEtablissement;
      if (typeVoieEtablissement) {
        tmpStreet = `${typeVoieEtablissement} ${libelleVoieEtablissement}`;
      }

      const postalCodeMetropolitanFrance = checkEligibilyCodePostal(
        codePostalEtablissement
      );

      if (!postalCodeMetropolitanFrance) {
        setSiretStatus(false);
        message('company_outside_mainland', [60]);
      } else {
        const formattedResult = {
          customer: {
            company_status: etatAdministratifUniteLegale,
            naf: activitePrincipaleUniteLegale,
            siret,
            company_creation_date:
              dateCreationEtablissement && moment(dateCreationEtablissement),
            company_name: denominationUniteLegale || nomUniteLegale,
            address: {
              ...formValues?.customer?.address,
              number: numeroVoieEtablissement,
              street: tmpStreet,
              postal_code: codePostalEtablissement,
              additionnal: complementAdresseEtablissement,
              city: libelleCommuneEtablissement
            }
          }
        };
        if (prenomUsuelUniteLegale && nomUniteLegale && tmpGender) {
          formattedResult.customer = {
            ...formattedResult.customer,
            first_name: prenomUsuelUniteLegale,
            last_name: nomUniteLegale,
            gender: tmpGender
          };
        }

        form.setFieldsValue({
          ...formValues,
          ...formattedResult
        });
        setFormValues({
          ...formValues,
          ...formattedResult,
          customer: { ...formValues.customer, ...formattedResult.customer }
        });
        setSiretStatus(true);
      }
    } catch (e) {
      if (e.response) message('unknown_siren_siret_999', [60]);
    }
    setInseeLoading(false);
  };
  const getRnaInfo = async (code) => {
    if (await verifyCustomer(code)) {
      antMessage.error(
        'Il semble que votre prospect(e) n’est pas éligible à nos produits, vous pouvez contacter le siège pour plus de renseignements.'
      );
      return;
    }
    setIsOkCustomer(true);
    setInseeLoading(true);
    try {
      const {
        data: {
          association: {
            id_association,
            date_creation,
            adresse_numero_voie,
            adresse_type_voie,
            adresse_libelle_voie,
            adresse_code_postal,
            adresse_libelle_commune,
            titre,
            titre_court,
            objet,
            dirigeant_civilite,
            telephone,
            email
          }
        }
      } = await dispatchAPI('GET', {
        url: `/rna/${code.toUpperCase()}`
      });
      setRnaStatus(true);
      let tmpStreet = adresse_libelle_voie;
      if (adresse_type_voie) {
        tmpStreet = `${adresse_type_voie} ${adresse_libelle_voie}`;
      }
      let tmpGender;
      if (dirigeant_civilite === 'PM' || dirigeant_civilite === 'TM') {
        tmpGender = 'MISTER';
      }
      if (dirigeant_civilite === 'PF' || dirigeant_civilite === 'TF') {
        tmpGender = 'MADAME';
      }
      let tmpRepresentative;
      if (dirigeant_civilite === 'TF' || dirigeant_civilite === 'TM') {
        tmpRepresentative = 'TREASURER';
      }
      if (dirigeant_civilite === 'PF' || dirigeant_civilite === 'TM') {
        tmpRepresentative = 'PRESIDENT';
      }

      const postalCodeMetropolitanFrance =
        checkEligibilyCodePostal(adresse_code_postal);

      if (!postalCodeMetropolitanFrance) {
        setSiretStatus(false);
        message('association_outside_mainland', [60]);
      } else {
        const formattedResult = {
          customer: {
            naf: objet,
            rna: id_association,
            company_creation_date: date_creation && moment(date_creation),
            company_name: titre || titre_court,
            address: {
              ...formValues?.customer?.address,
              number: adresse_numero_voie,
              street: tmpStreet,
              postal_code: adresse_code_postal,
              city: adresse_libelle_commune
            }
          }
        };
        if (tmpGender) {
          formattedResult.customer = {
            ...formattedResult.customer,
            gender: tmpGender,
            representative: tmpRepresentative
          };
        }
        if (email && telephone) {
          formattedResult.customer = {
            ...formattedResult.customer,
            phone_number: telephone,
            email
          };
        }
        form.setFieldsValue({
          ...formValues,
          ...formattedResult
        });
        setFormValues({
          ...formValues,
          ...formattedResult,
          customer: { ...formValues.customer, ...formattedResult.customer }
        });
      }
    } catch (e) {
      setRnaStatus(false);
      if (e.response) message('unknown_rna_999', [60]);
    }
    setInseeLoading(false);
  };

  const handleChange = async (
    {
      customer,
      number_claims,
      termination_by_company,
      exploitation_responsibility,
      paperwork_fee,
      empty_unoccupied_works,
      risk_been_insured,
      company_been_insured,
      activities,
      sites,
      classic_accident_insurance,
      fraction_type,
      subscriber_type
    },
    values
  ) => {
    if (fraction_type === 'MONTHLY') {
      form.setFieldsValue({ payment_type: 'DIRECTDEBIT' });
      setFormValues({
        ...formValues,
        payment_type: 'DIRECTDEBIT'
      });
    }
    if (fraction_type === 'YEARLY') {
      form.setFieldsValue({ payment_type: 'CARD' });
      setFormValues({
        ...formValues,
        payment_type: 'CARD'
      });
    }
    if (!classic_accident_insurance) {
      form.setFieldsValue({ reinforced_accident_insurance: false });
      setFormValues({
        ...formValues,
        reinforced_accident_insurance: false
      });
    }
    if (
      (customer?.siret &&
        customer.siret.length !== 14 &&
        customer.siret.length !== 9) ||
      customer?.siret?.length === 0
    ) {
      setSiretStatus(false);
    }
    if (
      customer?.siret &&
      (customer.siret.length === 14 || customer.siret.length === 9)
    ) {
      if (customer?.siret && customer.siret === '99999999999999') {
        setIsOkCustomer(true);
        setSiretStatus(true);
        form.setFieldsValue({
          ...formValues,
          customer: {
            ...formValues.customer,
            siret: '99999999999999'
          }
        });
        setFormValues({
          ...formValues,
          customer: {
            ...formValues.customer,
            siret: '99999999999999'
          },
          ...chosenPckg
        });
      } else if (!customer?.siret.startsWith('999999999'))
        await getSiretInfo(customer.siret);
    }
    if (customer?.rna && customer.rna.length !== 10) {
      setRnaStatus(false);
    }
    if (customer?.rna?.length === 0) {
      setRnaStatus(false);
    }
    if (customer?.rna && customer.rna.length === 10) {
      if (customer?.rna && customer.rna === '9999999999') {
        setIsOkCustomer(true);
        setRnaStatus(true);
        form.setFieldsValue({
          ...formValues,
          customer: {
            ...formValues.customer,
            rna: '9999999999'
          }
        });
        setFormValues({
          ...formValues,
          customer: {
            ...formValues.customer,
            rna: '9999999999'
          },
          ...chosenPckg
        });
      } else if (!customer?.rna.startsWith('999999')) {
        setRnaStatus(true);
      }
    } else if (termination_by_company === false) {
      form.setFieldsValue({
        termination_reason_by_company: undefined,
        termination_date_by_company: undefined
      });
      setFormValues({
        ...formValues,
        termination_reason_by_company: undefined,
        termination_by_company: false,
        termination_date_by_company: undefined,
        ...chosenPckg
      });
    } else if (exploitation_responsibility === false) {
      form.setFieldsValue({ surface_area: 0 });
      setFormValues({
        ...formValues,
        surface_area: 0,
        exploitation_responsibility: false,
        ...chosenPckg
      });
    } else if (paperwork_fee === 0) {
      form.setFieldsValue({ paperwork_fee_payment: undefined });
      setFormValues({
        ...formValues,
        paperwork_fee_payment: undefined,
        paperwork_fee: 0,
        ...chosenPckg
      });
    } else if (company_been_insured === false) {
      form.setFieldsValue({
        termination_by_company: false,
        company_been_insured: false,
        company_in_creation: true,
        termination_reason_by_company: undefined,
        termination_date_by_company: undefined,
        last_company_name: undefined,
        last_contract_number: undefined,
        last_termination_date: undefined
      });
      setFormValues({
        ...formValues,
        termination_by_company: false,
        company_been_insured: false,
        company_in_creation: true,
        termination_reason_by_company: undefined,
        termination_date_by_company: undefined,
        ...chosenPckg
      });
    } else if (company_been_insured === true) {
      form.setFieldsValue({ company_in_creation: false });
      setFormValues({
        ...formValues,
        company_in_creation: false,
        company_been_insured: true,
        last_company_name: undefined,
        last_contract_number: undefined,
        last_termination_date: undefined,
        ...chosenPckg
      });
    } else if (
      (type.includes('mrp') || assoType === 'localRc') &&
      (sites || activities)
    ) {
      const levels = [];
      let minLevel = 1;
      const selectedActivities = (values?.activities || activities || []).map(
        (a) => a.split(' - ')[0]
      );
      selectedActivities.forEach((activityName) => {
        const activity = codesNaf.find((act) => act.name === activityName);
        if (activity?.protection_level?.non_isolated_site > minLevel)
          minLevel = activity?.protection_level.non_isolated_site;
      });
      selectedActivities.forEach((activityName) => {
        const activity = codesNaf.find((act) => act.name === activityName);
        if (activity?.protection_level?.non_isolated_site > minLevel)
          minLevel = activity?.protection_level.non_isolated_site;
      });

      if (sites?.length || values?.sites?.length) {
        (values.sites || sites).forEach((s) => {
          let protection_level = minLevel;
          if (s?.isolated_site && protection_level < 3) protection_level += 1;
          levels.push(protection_level);
        });
      }
      setFormValues({
        ...formValues,
        sites: (values?.sites || sites).map((s, idx) => ({
          ...s,
          protection_level: levels[idx]
        }))
      });
      form.setFieldsValue({
        sites: (values?.sites || sites).map((s, idx) => ({
          ...s,
          protection_level: levels[idx]
        }))
      });
    } else {
      if (number_claims) {
        const claims = [];
        for (let i = 0; i < number_claims; i += 1) {
          claims.push('');
        }
        form.setFieldsValue({ claims });
      }
      if (number_claims === 0) {
        form.setFieldsValue({ claims: [{}] });
        setFormValues({ ...formValues, ...values, ...chosenPckg, claims: [] });
      } else {
        setFormValues({
          ...formValues,
          ...values,
          ...chosenPckg
        });
      }
      if (empty_unoccupied_works === true) {
        form.setFieldsValue({ guarantee_type: 'TYPE1' });
        setFormValues({
          ...formValues,
          ...values,
          ...chosenPckg,
          guarantee_type: 'TYPE1'
        });
      }
      if (subscriber_type === 'LE_BLDG_SELLER_OF_GOODS') {
        form.setFieldsValue({
          guarantee_type: 'TYPE1',
          payment_type: 'CARD',
          deductible_cost_index: '4,5x',
          fraction_type: 'YEARLY'
        });
        setFormValues({
          ...formValues,
          ...values,
          ...chosenPckg,
          payment_type: 'CARD',
          guarantee_type: 'TYPE1',
          deductible_cost_index: '4,5x',
          fraction_type: 'YEARLY'
        });
        notification.info({
          message: t('contracts.messages.info_seller_of_goods'),
          duration: 15
        });
      }
      if (risk_been_insured === false)
        form.setFieldsValue({
          last_company_name: null,
          last_contract_number: null,
          last_termination_date: null
        });
    }
    if (type === 'rcngawk') {
      const { corrected, warnings } = applyRcngawkRules(values, t);
      warnings.forEach((w) => notification.warning(w));
      form.setFieldsValue(corrected);
      setFormValues({ ...formValues, ...corrected, ...chosenPckg });
      if (!lockFirstEstimation && current < 2) {
        getEstimation(corrected, type, supplier);
      }
    }
    if (purpose !== 'create') {
      setFormValues({
        ...formValues,
        ...values
      });
    }
  };

  useEffect(() => {
    if (type === 'association' && assoType === 'localRc') {
      const theftVandalism =
        formValues?.fire_explosion_attack_climatic_event * 0.5;
      const waterDamage =
        formValues?.fire_explosion_attack_climatic_event * 0.5;
      const brokenWindows =
        formValues?.fire_explosion_attack_climatic_event * 0.2;
      let packageAsso = form.getFieldValue(['PACKAGE_1']);
      packageAsso = {
        ...packageAsso,
        theft_vandalism: theftVandalism,
        water_damage: waterDamage,
        broken_windows: brokenWindows
      };
      setFormValues({
        ...formValues,
        PACKAGE_1: packageAsso
      });
      form.setFieldsValue({
        PACKAGE_1: packageAsso
      });
    }
  }, [formValues?.fire_explosion_attack_climatic_event]);

  const handleCancel = () => {
    setFormValues(null);
    setExemptions([]);
    navigate(-1);
  };

  const openNotificationWithIcon = (notificationType, description) => {
    notification[notificationType]({
      description,
      duration: 7,
      key: 'oneTime'
    });
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      if (
        type === 'rcp' &&
        supplier === 'ml' &&
        estimatedCost.total_monthly_customer - estimatedCost.remuneration <
          8.5 &&
        formValues?.fraction_type === 'MONTHLY'
      ) {
        openNotificationWithIcon(
          'warning',
          t('contracts.form.premium_too_low')
        );
      } else {
        setCurrent(current + 1);
        document
          .getElementById('mainContent')
          .scrollTo({ behavior: 'smooth', top: 0 });
      }
    } catch (e) {
      if (e.errorFields && e.errorFields.length)
        form.scrollToField(e.errorFields[0].name);
    }
  };

  useEffect(() => {
    if (estimationAvoider !== type && !lockFirstEstimation && current < 2) {
      if (type === 'rcngawk') return;
      getEstimation(debouncedFormValues, type, supplier);
    }
  }, [debouncedFormValues, type]);

  useEffect(() => {
    if (formValues) {
      setExemptions(
        checkExemption({
          type,
          values: formValues,
          initialContractValues,
          codesNaf,
          supplier,
          estimation: estimatedCost,
          errorGetCities,
          errorGetQPV,
          isQPV,
          activitiesHiscox
        })
      );
      if (
        (formValues.contribution_adjustment < 0 &&
          formValues.commission_fee > 10) ||
        (formValues.contribution_adjustment < 0 && formValues.broker_fee > 0)
      ) {
        openNotificationWithIcon(
          'warning',
          t('contracts.form.contribution_adjustment_message')
        );
      }
    }
  }, [formValues, estimatedCost]);

  useEffect(() => {
    if (!formValues) {
      if (dataModal) {
        setFormValues({ ...defaultValues, ...dataModal });
        form.setFieldsValue({ ...defaultValues, ...dataModal });
      } else {
        setFormValues(defaultValues);
        form.setFieldsValue(defaultValues);
      }
    }
    return () => {
      setDataModal(undefined);
      setFormValues(null);
      form.resetFields();
    };
  }, []);

  // Function when the modal is opening
  const openModal = async () => {
    try {
      await form.validateFields();
      setVisible(true);
    } catch (e) {
      if (e.errorFields && e.errorFields.length)
        form.scrollToField(e.errorFields[0].name);
    }
  };

  const isReductScreen = useMediaQuery({ query: '(max-width: 1320px)' });

  useEffect(() => {
    const handleResize = () => {
      setSize({ width: window.innerWidth });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!(current === 0 && type === 'mriml')) {
      const datePickerDiv = document.getElementsByClassName('ant-picker-input');
      if (datePickerDiv.length) {
        const datePickerCollection =
          document.getElementsByClassName('ant-picker-input');
        if (datePickerCollection.length) {
          for (let i = 0; i < datePickerCollection.length; i += 1) {
            const [datePicker] =
              datePickerCollection[i].getElementsByTagName('INPUT');
            if (datePicker)
              datePicker.addEventListener('keyup', (e) => {
                if (e.which !== 8) {
                  const numChars = datePicker.value.length;
                  if (numChars === 2 || numChars === 5) {
                    let thisVal = datePicker.value;
                    thisVal += '/';
                    datePicker.value = thisVal;
                  }
                }
              });
          }
        }
      }
    }
  }, [current, formValues, type]);

  useEffect(() => {
    const contractSteps = steps[type.toLowerCase()];
    if (!contractSteps) {
      return;
    }

    if (goToStep === 'last') {
      const lastStepIndex = contractSteps.length - 1;
      setCurrent(lastStepIndex);
    }
  }, [goToStep]);

  const hasExclusion = useMemo(() => {
    if (
      type === 'pnoml' &&
      formValues?.under_construction_duration === 'under_construction_plus4'
    ) {
      return true;
    }
    if (!exemptions.length) {
      return false;
    }

    return !!exemptions?.includes(EXCLUSION_EXEMPTION_ITEM);
  }, [exemptions]);

  return (
    <>
      <AnalyzeModal
        handleSubmit={(tracfin) => handleSubmit(null, modalExemption, tracfin)}
        createQuote
        type={type}
      />
      <PageHeader
        title={t(`contracts.form.title.${purpose}.${type.toLowerCase()}`)}
      />
      <ContentCustom>
        <Steps
          current={current}
          items={(steps[type.toLowerCase()] || []).map((item) => ({
            title: item.title
          }))}
        />
        <Spin spinning={isLoading}>
          <Form
            {...verticalLayout}
            onFinish={() => {
              if (exemptions.length) {
                openModal(false);
              } else {
                setAnalyzeModalVisible(true);
              }
            }}
            onValuesChange={handleChange}
            form={form}
            scrollToFirstError
            layout={
              steps[type.toLowerCase()][current].preRequisites ||
              steps[type.toLowerCase()][current].horizontal
                ? 'horizontal'
                : 'vertical'
            }
          >
            <div className="step-content">
              {steps[type.toLowerCase()][current].subTitle}
              {(steps[type.toLowerCase()][current].content || []).map((field) =>
                generateFields('contracts', field)
              )}
            </div>
            <Form.Item wrapperCol={24}>
              {!isOkCustomer && (
                <Row>
                  <Typography.Text style={{ color: 'red', marginBottom: 15 }}>
                    Il semble que votre prospect(e) n’est pas éligible à nos
                    produits, vous pouvez contacter le siège pour plus de
                    renseignements.
                  </Typography.Text>
                </Row>
              )}
              <Row>
                {current !== 0 && (
                  <Button
                    type="primary"
                    style={{
                      marginRight: 10,
                      width: isReductScreen ? 32 : 'auto',
                      padding: isReductScreen ? '4px 3px' : '4px 15px'
                    }}
                    onClick={handlePrev}
                  >
                    <LeftOutlined
                      style={{ marginLeft: isReductScreen ? 0 : 8 }}
                    />
                    <span
                      style={{
                        display: isReductScreen ? 'none' : 'inline'
                      }}
                    >
                      {`${t('buttons.prev')}`}
                    </span>
                  </Button>
                )}
                {current !== steps[type.toLowerCase()].length - 1 && (
                  <Button
                    type="primary"
                    onClick={handleNext}
                    disabled={hasExclusion || !eligiblePricing}
                    style={{
                      marginRight: 10,
                      width: isReductScreen ? 32 : 'auto',
                      padding: isReductScreen ? '4px 3px' : '4px 15px'
                    }}
                  >
                    <span
                      style={{
                        display: isReductScreen ? 'none' : 'inline'
                      }}
                    >
                      {`${t('buttons.next')}`}
                    </span>
                    <RightOutlined
                      style={{ marginLeft: isReductScreen ? 0 : 8 }}
                    />
                  </Button>
                )}
                {current === steps[type.toLowerCase()].length - 1 &&
                  isOkCustomer &&
                  (type !== 'rcngawk' ? (
                    <>
                      <Button
                        type="add"
                        htmlType="submit"
                        style={{ margin: 0 }}
                        loading={isLoading}
                        disabled={hasExclusion}
                      >
                        <CheckOutlined />
                        {purpose === 'duplicate_amendment' ||
                        purpose === 'amendment'
                          ? `${t(
                              `buttons.${
                                exemptions.length && !hasExclusion
                                  ? 'save_exemption_amendment'
                                  : 'save_amendment'
                              }`
                            )} `
                          : `${t(
                              `buttons.${
                                exemptions.length && !hasExclusion
                                  ? 'save_exemption'
                                  : 'save_quote'
                              }`
                            )} `}
                      </Button>
                      <ExemptionModal
                        setFile={setFile}
                        open={visible}
                        setVisible={setVisible}
                        openModal={openModal}
                        exemptions={exemptions}
                        setModalExemption={setModalExemption}
                        isEmpty={
                          formValues?.is_vacant ||
                          formValues?.empty_unoccupied_works
                        }
                        hasClaims={formValues?.number_claims > 0}
                      />
                    </>
                  ) : (
                    <RcngawkExemptionModal
                      setFile={setFile}
                      file={file}
                      open={visible}
                      setVisible={setVisible}
                      openModal={openModal}
                      exemptions={exemptions}
                      setModalExemption={setModalExemption}
                    />
                  ))}
                <Button
                  style={{
                    margin: '0 10px 0 0'
                  }}
                  type="link"
                  danger
                  onClick={handleCancel}
                >
                  {current === 0
                    ? `${t('buttons.back')} `
                    : `${t('buttons.cancel')} `}
                  <CloseOutlined />
                </Button>
              </Row>
            </Form.Item>
          </Form>
        </Spin>
      </ContentCustom>

      {steps[type.toLowerCase()][current].estimationCard &&
        (!estimatedCost?.error ||
          !!estimatedCost?.error?.forced_exemptions ||
          !!estimatedCost?.error?.['address.city']) && <EstimationCard />}

      <style>
        {`
        .step-content {
          margin: 24px 0;
          max-width: ${
            steps[type.toLowerCase()][current].preRequisites ? 'none' : '100%'
          };
        }
        .ant-form-item-label {
          white-space: normal;
        }
        .ant-form-item-label > label {
          height: auto;
          min-height: 32px;
        }
      `}
      </style>
      <ExclusionModal exemptions={exemptions ?? []} />
    </>
  );
};

CreateUpdateQuote.propTypes = {
  purpose: PropTypes.string.isRequired
};

export default CreateUpdateQuote;
